import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import AreaPage from "./pages/AreaPage";

/* WEBSITE */
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import TrackRepair from "./pages/TrackRepair";
import Brands from "./pages/Brand";
import Models from "./pages/Models";
import BookRepair from "./pages/Book";
import CustomerLogin from "./pages/CustomerLogin";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import ThankYou from "./pages/ThankYou";

/* ADMIN */
import AdminLogin from "./pages/admin/AdminLogin";
import AdminRegister from "./pages/admin/AdminRegister";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/Orders";
import AdminCustomers from "./pages/admin/Cutomer";
import AdminAddModelBrands from "./pages/admin/Brands";
import AdminLayout from "./layouts/AdminLayout";
import Technicians from "./pages/admin/Technicians";
import AdminServices from "./pages/admin/AdminServices";
import AdminRFQ from "./pages/admin/RFQ";

function getMetaByPath(pathname) {
  if (pathname === "/") {
    return {
      title: "Servizy | Doorstep Mobile Repair in Bangalore",
      description:
        "Servizy offers fast and trusted doorstep mobile repair service in Bangalore for screen, battery, charging, and software issues.",
    };
  }

  const staticMeta = {
    "/about": {
      title: "About Servizy | Mobile Repair Experts",
      description:
        "Learn about Servizy and our expert mobile repair technicians providing reliable doorstep smartphone service across Bangalore.",
    },
    "/services": {
      title: "Mobile Repair Services | Servizy Bangalore",
      description:
        "Explore Servizy mobile repair services including screen replacement, battery replacement, charging port repair, and software fixes.",
    },
    "/contact": {
      title: "Contact Servizy | Book Mobile Repair",
      description:
        "Contact Servizy to book doorstep mobile repair in Bangalore. Quick support for all major smartphone brands.",
    },
    "/privacy-policy": {
      title: "Privacy Policy | Servizy",
      description:
        "Read the Servizy privacy policy for details on how we collect, use, and protect customer information.",
    },
    "/terms": {
      title: "Terms & Conditions | Servizy",
      description:
        "Review Servizy terms and conditions for mobile repair bookings, payments, and service coverage.",
    },
    "/thank-you": {
      title: "Thank You | Servizy",
      description:
        "Thank you for choosing Servizy. Your mobile repair booking request has been received.",
    },
    "/track": {
      title: "Track Repair Status | Servizy",
      description:
        "Track your Servizy mobile repair status using your tracking details and stay updated in real time.",
    },
    "/login": {
      title: "Customer Login | Servizy",
      description:
        "Login to your Servizy customer account to manage bookings and track your mobile repair request.",
    },
    "/brands": {
      title: "Choose Your Mobile Brand | Servizy",
      description:
        "Select your smartphone brand and continue with Servizy doorstep mobile repair booking in Bangalore.",
    },
  };

  if (staticMeta[pathname]) {
    return staticMeta[pathname];
  }

  if (pathname.startsWith("/models/")) {
    return {
      title: "Select Model | Servizy Mobile Repair",
      description:
        "Choose your mobile model and view available repair options with Servizy doorstep smartphone service.",
    };
  }

  if (pathname.startsWith("/book/")) {
    return {
      title: "Book Mobile Repair | Servizy",
      description:
        "Book your doorstep mobile repair with Servizy for quick service, expert technicians, and transparent pricing.",
    };
  }

  if (pathname.startsWith("/track/")) {
    return {
      title: "Repair Tracking Details | Servizy",
      description:
        "View your Servizy repair tracking details and current repair progress.",
    };
  }

  return {
    title: "Servizy | Mobile Repair Service",
    description: "Servizy provides reliable doorstep mobile repair services in Bangalore.",
  };
}

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");
  const isLoginPage = location.pathname === "/login";
  const isAreaPagePath =
    location.pathname.startsWith("/mobile-repair-service-in-") ||
    location.pathname.startsWith("/mobile-repair-service-in/");
  const isBrandLandingPath = location.pathname.endsWith(
    "-mobile-repair-in-bangalore"
  );
  const hasPageLevelSeo =
    location.pathname === "/" ||
    location.pathname === "/about" ||
    location.pathname === "/services" ||
    location.pathname === "/contact" ||
    location.pathname === "/brands" ||
    isAreaPagePath ||
    isBrandLandingPath;

  const LegacyAreaRoute = () => {
    const legacyPrefix = "/mobile-repair-service-in-";

    if (location.pathname.startsWith(legacyPrefix)) {
      return <AreaPage />;
    }

    return <Navigate replace to="/" />;
  };

  const SlashAreaRedirect = () => {
    const { area } = useParams();
    return <Navigate replace to={`/mobile-repair-service-in-${area}`} />;
  };

  const SingleSegmentRoute = () => {
    const { slug = "" } = useParams();

    if (slug.startsWith("mobile-repair-service-in-")) {
      return <AreaPage />;
    }

    if (slug.endsWith("-mobile-repair-in-bangalore")) {
      const brand = slug.replace(/-mobile-repair-in-bangalore$/, "");
      return <Models forcedBrand={decodeURIComponent(brand)} />;
    }

    return <Navigate replace to="/" />;
  };

  const pageMeta = getMetaByPath(location.pathname);

  return (
    <>
      {!isAdminPage && !hasPageLevelSeo && (
        <Helmet>
          <title>{pageMeta.title}</title>
          <meta name="description" content={pageMeta.description} />
        </Helmet>
      )}

      {!isAdminPage && <Header />}
      <ScrollToTop />

      <Routes>
        {/* AREA ROUTES */}
        <Route path="/mobile-repair-service-in/:area" element={<SlashAreaRedirect />} />

        {/* WEBSITE */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/thank-you" element={<ThankYou />} />

        <Route path="/track" element={<TrackRepair />} />
        <Route path="/track/:trackingId/:phone" element={<TrackRepair />} />

        <Route path="/login" element={<CustomerLogin />} />

        <Route path="/brands" element={<Brands />} />
        <Route path="/models/:brand" element={<Models />} />
        <Route path="/:slug" element={<SingleSegmentRoute />} />

        <Route path="/book/:brand/:model" element={<BookRepair />} />

        {/* ADMIN */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/register" element={<AdminRegister />} />

        <Route path="/admin" element={<AdminLayout />}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="rfq" element={<AdminRFQ />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="addmodelbrands" element={<AdminAddModelBrands />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="technicians" element={<Technicians />} />
        </Route>

        {/* Fallback: redirect legacy/unknown routes */}
        <Route path="*" element={<LegacyAreaRoute />} />
      </Routes>

      {!isAdminPage && !isLoginPage && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
